// ---------------------------------------------------------------------------
// SKIPPED FIELDS
//
// Figma: Corporate Account — Discovery. The detail pages introduce fields with no backing
// column on `clients`. Per eng-manager direction they render blank rather than faked — the
// cell keeps its width and the row keeps its height (see `Skipped` in
// CorporateUsers/shippers/blocks.js), so restoring one is a matter of swapping the
// accessor back in.
//
// `values` records the enums the designer annotated on the Figma canvas. There is nowhere
// to store them yet; they are kept here so the work survives until the columns land.
//
// NOTE: the NetSuite ID that used to sit in the account banner is gone, not skipped — the
// designer confirmed it was an old iteration. NetSuite now lives in Company Info →
// Identifier Details as ns_customer_id / ns_location_id / ns_profit_center / ns_vendor_id,
// which `ExternalIdentifiers` already renders from external_identifier_definitions.
//
// To find every skipped field: grep for SKIPPED_FIELDS and `Skipped` in this directory.
// ---------------------------------------------------------------------------
export const SKIPPED_FIELDS = {
    // Company Info → Billing. This section survives because Name, Email, Summarized billing and
    // Card info are real; only these three columns are blank.
    billingCode: { label: 'Billing Code' },
    terms: { label: 'Terms', values: ['Due on Receipt', 'Net 15', 'Net 30', 'Net 60'] },
    cadence: { label: 'Cadence' },
};

// Buttons the design draws that have nothing to call. Each rendered disabled, which promises a
// capability the app does not have — a greyed-out button still reads as "coming", and a user
// will try it. Removed on the same rule as REMOVED_SECTIONS; restore one when its backing
// action exists, at the site named below (grep REMOVED_ACTIONS).
export const REMOVED_ACTIONS = {
    sendReminder: {
        label: 'Send reminder',
        site: 'index.jsx — account banner, beside Edit',
        blockedOn: 'nothing sends reminders, and no go-live state exists to remind anyone about',
    },
    testIntegration: {
        label: 'Test',
        site: 'tabs/Integrations.jsx — order-entry card',
        blockedOn: 'no test-order capability behind dispatch_integration_type',
    },
    configureIntegration: {
        label: 'Configure',
        site: 'tabs/Integrations.jsx — order-entry card',
        blockedOn: 'integration settings are not editable from this page',
    },
    startOnMap: {
        label: 'Start on the map →',
        site: 'tabs/Overview.jsx — Markets awarded section',
        blockedOn: 'the Map view that builds delivery profiles is a later sprint',
    },
    previewRateShop: {
        label: 'Preview rate shop →',
        site: 'tabs/Overview.jsx — Rate Shop routing card',
        blockedOn: 'no rate-shop preview exists to open',
    },
    addContact: {
        label: 'Add contact',
        site: 'tabs/CompanyInfo.jsx — Key contacts footer',
        blockedOn: 'the three contact slots are fixed columns on `clients`; there is no fourth',
    },
};

// Whole Overview sections pulled from the page rather than left as titled empty cards. Each one
// had no backing data at all, so there was no field to reserve space for — restoring one means
// putting the section back in Overview.jsx (the comments there mark where) once it has a source.
export const REMOVED_SECTIONS = {
    bannerOwner: {
        title: 'Account banner → OWNER',
        showed: 'a person icon and "OWNER: <name>"',
        blockedOn: 'designer: pull from whoever created the record — scoped to the Roles and Permissions sprint',
    },
    clientIdentity: {
        title: 'Company Info → Client identity',
        showed: 'Contract (+ start–expiration sub-line), Commodity, Vertical, Annual Est. Volume, Markets awarded',
        blockedOn: 'none of the five has a column on `clients`',
        // Enums the designer supplied; no storage for them yet, kept so the work survives.
        values: {
            commodity: ['Furniture & Mattresses', 'Appliances & Electronics', 'Exercise Equipment'],
            vertical: ['2 wall', '3 wall', '4 wall', 'FP', 'E2E', 'SaaS'],
            marketsAwarded: [
                'Northeast',
                'Mid Atlantic',
                'Southeast',
                'Gulf',
                'Midwest',
                'Mountain Region',
                'Pacific Northwest',
                'California',
            ],
        },
    },
    trackingPortalCard: {
        title: 'Integrations → Onward tracking portal',
        showed: 'a Seats count and a Status pill',
        blockedOn: 'no seat, invite or portal-status record exists',
    },
    integrationDetail: {
        title: 'Integrations → order-entry sub-line and "Last file"',
        showed: 'a setup-journey sub-line and a last-file timestamp',
        blockedOn: 'no ingest log; only `clients.dispatch_integration_type` is stored',
    },
    onboardingChecklist: {
        title: 'Onboarding checklist',
        showed: 'six chips — Integrations, Company info, Tariffs + tags, Key documents, Operational, Comms prefs',
        blockedOn: 'no per-area completion state; the chips can only ever render neutral',
    },
    blockers: {
        title: "What's blocking go-live",
        showed: 'a single status line',
        blockedOn: 'nothing records go-live blockers',
    },
    training: {
        title: 'Training',
        showed: '"0 of 8 modules trained" plus an "in progress · gated before go-live" sub-line',
        blockedOn: 'no training module or progress record exists',
    },
    notes: {
        title: 'Notes & history',
        showed: 'a "Log a note, call, or decision…" input and an Add note button',
        blockedOn:
            '`clients.onward_admin_notes` is a single text column for Onward admins, not a per-account history log',
    },
};

// Genuine empty-state copy: shown only when the real query comes back with nothing, never as a
// stand-in for data we did not fetch.
export const EMPTY_STATE_COPY = {
    markets:
        'No profiles yet. Build one on the map — pick commodity × service, carriers and tariffs wire in one stroke.',
};

export const EMPTY_VALUE = '—';

export const TABS = [
    { slug: 'overview', label: 'Overview' },
    { slug: 'company-info', label: 'Company Info' },
    { slug: 'integrations', label: 'Integrations' },
    { slug: 'tariffs-tags', label: 'Tariffs & Tags' },
    { slug: 'key-docs', label: 'Key Docs' },
    { slug: 'operational', label: 'Operational' },
    { slug: 'communications', label: 'Communications' },
];

// Overview → Auto-assign waterfall. Fixed copy from the design; the ordering is not yet editable.
export const ROUTING_WATERFALL = [
    { title: 'Primary carrier', badge: '1st', detail: 'the ranked cascade on the map' },
    { title: 'Speed to customer', detail: 'fastest transit from facility to door' },
    { title: 'Best rate', detail: 'lowest priced stored tariff' },
    { title: 'Quality', detail: 'best overall KPI score' },
];

// Operational → sub-tabs. Each renders an existing Account component unchanged; they resolve
// the shipper from context, which index.jsx has already overridden.
export const OPERATIONAL_TABS = [
    { key: 'stores', label: 'Stores' },
    { key: 'skus', label: 'SKUs' },
    { key: 'sla', label: 'SLA Requirements' },
    { key: 'teammates', label: 'Teammates' },
    { key: 'surveys', label: 'Surveys' },
    { key: 'communication', label: 'Communication' },
];
